import { z } from "zod";
import { defineEvent } from "./_define";
import {
  baseEnvelopeSchema,
  billingPeriodSchema,
  productNormalizedSchema,
  subscriptionStatusSchema,
} from "./_envelope";

const productValueSchema = z.union([productNormalizedSchema, z.string()]);

const subscriptionEnvelope = baseEnvelopeSchema.extend({
  product_key: z.string().optional(),
  billing_period: z.union([billingPeriodSchema, z.string()]).optional(),
  // Integer cents, the unit_amount of the current subscription's price for
  // one billing period. Stamped on every subscription event so dashboards
  // can compute weighted revenue without joining to a price table.
  price_cents: z.union([z.string(), z.number()]).optional(),
  customer_email: z.string().optional(),
  paywall_variant: z.string().optional(),
  journey_id: z.string().optional(),
  trial_days: z.union([z.string(), z.number()]).optional(),
  stripe_event_id: z.string().optional(),
  // Orthogonal signals decoupled from `status` (raw Stripe status). Server
  // events use native bool. See normalize-subscription-status helpers.
  cancels_at_period_end: z.union([z.string(), z.boolean()]).optional(),
  has_paid: z.union([z.string(), z.boolean()]).optional(),
});

export const subscriptionCreated = defineEvent({
  eventName: "subscription_created",
  eventVersion: 2,
  valueSchema: productValueSchema,
  metadataSchema: subscriptionEnvelope
    .extend({
      subscription_status: z.string().optional(),
      implied_mrr_cents: z.number().int().nonnegative().optional(),
      implied_immediate_revenue_cents: z.number().int().nonnegative().optional(),
    })
    .passthrough(),
});

// v3 (hard cutover): `value` is now the raw Stripe status (mirrors Stripe),
// and cancellation/payment are carried as the orthogonal `cancels_at_period_end`
// and `has_paid` metadata booleans rather than folded into the value (no more
// blended `canceled_but_active`).
export const subscriptionStatusUpdated = defineEvent({
  eventName: "subscription_status_updated",
  eventVersion: 3,
  valueSchema: subscriptionStatusSchema.or(z.string()),
  metadataSchema: subscriptionEnvelope
    .extend({
      // Snapshot event — it fires on ANY subscription change. "true" only
      // when the status itself flipped, so transition queries can filter.
      status_changed: z.union([z.string(), z.boolean()]).optional(),
    })
    .passthrough(),
});

export const subscriptionCanceled = defineEvent({
  eventName: "subscription_canceled",
  eventVersion: 2,
  valueSchema: productValueSchema,
  metadataSchema: subscriptionEnvelope
    .extend({
      cancel_reason_code: z.string().optional(),
      cancel_feedback: z.string().optional(),
      save_offer_outcome: z.enum(["accepted", "declined"]).optional(),
      save_offer_id: z.string().optional(),
      cancel_source: z.string().optional(),
      cancel_type: z
        .enum(["free_trial", "converted_never_paid", "paid", "unknown"])
        .or(z.string())
        .optional(),
      was_trialing: z.union([z.string(), z.boolean()]).optional(),
      n_visits_lifetime: z.union([z.string(), z.number()]).optional(),
      subscription_age_days: z.union([z.string(), z.number()]).optional(),
      is_upgrade_replacement: z.union([z.string(), z.boolean()]).optional(),
    })
    .passthrough(),
});

export const subscriptionRevenueRecognized = defineEvent({
  eventName: "subscription_revenue_recognized",
  eventVersion: 2,
  valueSchema: z.number().int().nonnegative(),
  metadataSchema: subscriptionEnvelope
    .extend({
      revenue_period_days: z.union([z.string(), z.number()]).optional(),
      is_renewal: z.union([z.string(), z.boolean()]).optional(),
      is_trial_conversion: z.union([z.string(), z.boolean()]).optional(),
      currency: z.string().optional(),
      invoice_id: z.string().optional(),
      n_visits_lifetime: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

// v2: value is THIS refund's amount in cents (delta vs previous_attributes),
// so summing values equals total refunded. v1 logged Stripe's cumulative
// amount_refunded, double-counting earlier partials on multi-refund charges;
// the cumulative figure now rides in cumulative_refunded_cents.
export const subscriptionRefunded = defineEvent({
  eventName: "subscription_refunded",
  eventVersion: 2,
  valueSchema: z.number().int().nonnegative(),
  metadataSchema: subscriptionEnvelope
    .extend({
      refund_reason: z.string().optional(),
      is_partial: z.union([z.string(), z.boolean()]).optional(),
      original_charge_amount_cents: z.number().int().nonnegative().optional(),
      cumulative_refunded_cents: z.number().int().nonnegative().optional(),
      subscription_age_days: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const subscriptionChargeDisputed = defineEvent({
  eventName: "subscription_charge_disputed",
  eventVersion: 1,
  valueSchema: z.enum(["chargeback", "inquiry"]),
  metadataSchema: subscriptionEnvelope
    .extend({
      dispute_reason: z.string().optional(),
      amount_cents: z.number().int().nonnegative().optional(),
      subscription_age_days: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const cancelIntentSet = defineEvent({
  eventName: "cancel_intent_set",
  eventVersion: 1,
  valueSchema: z.enum(["user_initiated", "system_initiated"]),
  metadataSchema: subscriptionEnvelope
    .extend({
      current_product_key: z.string().optional(),
      current_billing_period: z.string().optional(),
      subscription_age_days: z.union([z.string(), z.number()]).optional(),
      n_visits_lifetime: z.union([z.string(), z.number()]).optional(),
      n_visits_30d: z.union([z.string(), z.number()]).optional(),
      was_trialing: z.union([z.string(), z.boolean()]).optional(),
      cancel_reason_code: z.string().optional(),
      cancel_feedback: z.string().optional(),
    })
    .passthrough(),
});

export const cancelIntentReversed = defineEvent({
  eventName: "cancel_intent_reversed",
  eventVersion: 1,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: subscriptionEnvelope
    .extend({
      subscription_age_days: z.union([z.string(), z.number()]).optional(),
      time_since_intent_ms: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const subscriptionPaymentFailed = defineEvent({
  eventName: "subscription_payment_failed",
  eventVersion: 1,
  valueSchema: z.enum(["trial_end", "renewal", "retry"]),
  metadataSchema: subscriptionEnvelope
    .extend({
      failure_code: z.string().optional(),
      attempt_count: z.union([z.string(), z.number()]).optional(),
      next_retry_at: z.union([z.string(), z.number()]).optional(),
      amount_due_cents: z.number().int().nonnegative().optional(),
      subscription_age_days: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const subscriptionPaymentRecovered = defineEvent({
  eventName: "subscription_payment_recovered",
  eventVersion: 1,
  valueSchema: z.number().int().nonnegative(),
  metadataSchema: subscriptionEnvelope
    .extend({
      time_in_past_due_ms: z.union([z.string(), z.number()]).optional(),
      attempt_count: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const trialEnded = defineEvent({
  eventName: "trial_ended",
  eventVersion: 1,
  valueSchema: z.enum(["converted", "canceled", "past_due"]),
  metadataSchema: subscriptionEnvelope
    .extend({
      n_visits_during_trial: z.union([z.string(), z.number()]).optional(),
      mrr_on_conversion_cents: z.number().int().nonnegative().optional(),
      n_children: z.union([z.string(), z.number()]).optional(),
      cancel_reason_code: z.string().optional(),
      cancel_feedback: z.string().optional(),
      save_offer_outcome: z.enum(["accepted", "declined"]).optional(),
      save_offer_id: z.string().optional(),
      failure_code: z.string().optional(),
    })
    .passthrough(),
});

export const subscriptionCanceledFeedback = defineEvent({
  eventName: "subscription_canceled_feedback",
  // v2 (OPS-276): breaking on both axes the taxonomy flags — the trigger moved
  // from `deleted` to (primarily) `updated`, and `value` went from a free-text
  // comment / "No reason given" to a Stripe cancellation `feedback` enum. The
  // version boundary lets the widget drop the historical junk rows.
  eventVersion: 2,
  valueSchema: z.string(),
  metadataSchema: subscriptionEnvelope
    .extend({
      // Absent on historical v1 rows; emitted explicitly by both webhook paths.
      taxonomy_version: z.literal(2).optional(),
      cancel_feedback: z.string().optional(),
      cancel_reason_code: z.string().optional(),
      cancel_comment: z.string().optional(),
    })
    .passthrough(),
});
